<template>
  <div class="tablet:z-11">
    <div ref="activatorRef">
      <session-button
        :icon-name="icon"
        :loading="loading"
        :disabled="disabled"
        :close-on-click-inside="closeOnClickInside"
        :button-title="buttonTitle"
        @click="toggleMenu()"
      />
    </div>
    <teleport :to="teleportTarget">
      <div
        v-if="isMenuOpen"
        ref="menuRef"
        data-test="session-button-with-dropdown-popup"
        class="fixed z-9999"
        :style="popupStyles"
        @click="handleMenuClick"
      >
        <slot />
      </div>
    </teleport>
  </div>
</template>

<script setup lang="ts">
import { DirectionType, IconName } from '@wui/common-library'
import { onBeforeUnmount, onMounted, ref, toRefs } from 'vue'

import { SessionButton } from '@/shared/components/buttons'
import { usePopupListeners } from '@/shared/composables/use-popup-listeners'
import { usePositionPopUpByDirection } from '@/shared/composables/use-position-popup'

const props = defineProps<{
  icon: IconName,
  loading?: boolean,
  direction: DirectionType,
  disabled?: boolean
  top?: boolean
  closeOnClickInside?: boolean
  buttonTitle?: string
}>()

const {
  icon,
  loading,
  direction,
  disabled,
  closeOnClickInside,
  buttonTitle,
} = toRefs(props)

const isMenuOpen = ref(false)
const activatorRef = ref<HTMLElement | null>(null)
const menuRef = ref<HTMLElement | null>(null)

const teleportTarget = ref<HTMLElement | string>('body')
const updateTeleportTarget = () => {
  teleportTarget.value = document.fullscreenElement as HTMLElement ?? document.body
}

onMounted(() => {
  updateTeleportTarget()
  document.addEventListener('fullscreenchange', updateTeleportTarget)
})

onBeforeUnmount(() => {
  document.removeEventListener('fullscreenchange', updateTeleportTarget)
})

const { popupStyles, updatePosition } = usePositionPopUpByDirection({
  activator: activatorRef,
  popup: menuRef,
  direction,
})

const closeMenu = () => {
  isMenuOpen.value = false
}

const toggleMenu = async () => {
  isMenuOpen.value = !isMenuOpen.value
  if (isMenuOpen.value) {
    await updatePosition()
  }
}

const handleMenuClick = () => {
  if (!closeOnClickInside.value) {
    return
  }
  closeMenu()
}

usePopupListeners({
  isOpen: isMenuOpen,
  activator: activatorRef,
  popup: menuRef,
  closeOnClickInside,
  updatePosition,
  close: closeMenu,
})

defineExpose({ close: closeMenu })
</script>
